import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import PencilEdit from '@/assets/icons/pencil-edit.svg';
import PlusSm from '@/assets/icons/plus-sm.svg';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { useOpeningDetails } from '@/contexts/opening-context';
import { formatShortDate } from '@/utils/date-util';

/**
 * Pure helper that decides whether to show the banner and returns
 * all data needed to render it.
 *
 * Inputs:
 * - daysLeft: number | null
 * - actualHires: number | null
 * - expectedHires: number | null
 * - endDate: string (already formatted)
 * - intl: react-intl's intl object for formatting
 *
 * Returns:
 * - { title?: string, description?: string, isSuccess?: boolean }
 */

function getHiringBannerData({
  daysLeft,
  actualHires,
  expectedHires,
  endDate,
  intl,
}: {
  daysLeft: number | null;
  actualHires: number | null;
  expectedHires: number | null;
  endDate: string;
  intl: IntlShape;
}) {
  const hasHiresInfo = actualHires != null && expectedHires != null;
  const goalEnded = daysLeft === 0;
  const meetsTarget = hasHiresInfo && actualHires >= expectedHires;
  const missesTarget = hasHiresInfo && actualHires < expectedHires;
  const endsTomorrow = daysLeft === 1;

  if (goalEnded && missesTarget) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal ended without reaching target',
        description:
          'Banner title: goal window closed and hire count is below the target',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal ended on {endDate} with {actualHires} out of {expectedHires} hires. Kindly create a new goal if your requirement is still open.',
          description:
            'Banner when the hiring period ended and hire count was below the goal',
        },
        { endDate, actualHires, expectedHires },
      ),
      isSuccess: false,
    };
  }

  if (goalEnded && meetsTarget) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal completed and target met!.',
        description:
          'Banner title: the goal period ended and hires met or beat the plan',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal ended on {endDate}. Create a new goal to continue tracking sourcing spend.',
          description:
            'Banner when the hiring goal ended and the target number of hires was met',
        },
        { endDate },
      ),
      isSuccess: true,
    };
  }

  if (endsTomorrow) {
    return {
      title: intl.formatMessage({
        defaultMessage: 'Hiring goal ends in 1 day',
        description:
          'Urgent reminder: the configured hiring period ends tomorrow',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'The hiring goal will end on {endDate}. Kindly consider extending the end date if the requirement is still open.',
          description: 'Warning when the hiring goal has one day remaining',
        },
        { endDate },
      ),
      isSuccess: false,
    };
  }

  return null;
}

function PerformanceStatusBanner() {
  const intl = useIntl();
  const { hiringGoal } = useOpeningDetails();

  if (!hiringGoal) return null;

  // Basic values
  const expectedDuration = hiringGoal.duration;
  const elapsedDays = hiringGoal.days_elapsed;
  const daysLeft =
    expectedDuration != null && elapsedDays != null
      ? expectedDuration - elapsedDays
      : null;

  const endDate = formatShortDate(hiringGoal.hire_by);
  const actualHires = hiringGoal.hire_stats?.actual;
  const expectedHires = hiringGoal.hire_stats?.expected;
  const endsTomorrow = daysLeft === 1;

  const bannerData = getHiringBannerData({
    daysLeft,
    actualHires,
    expectedHires,
    endDate,
    intl,
  });

  if (!bannerData) return null;

  const { title, description, isSuccess = false } = bannerData;

  return (
    <Banner
      color={isSuccess ? 'green' : 'amber'}
      title={title}
      description={description}
      button={
        <Button variant="secondary" asChild>
          <Link to={hiringGoal.url} target="_blank">
            <img
              src={endsTomorrow ? PencilEdit : PlusSm}
              alt={endsTomorrow ? 'Edit' : 'Add'}
            />
            {endsTomorrow ? (
              <FormattedMessage
                defaultMessage="Edit Goal"
                description="Banner CTA when goal ends soon: open Hire to adjust dates or targets"
              />
            ) : (
              <FormattedMessage
                defaultMessage="New Goal"
                description="Banner CTA: create a new hire-by window in Hire"
              />
            )}
          </Link>
        </Button>
      }
    />
  );
}

export { PerformanceStatusBanner };
